import React, { useEffect, useState, useContext } from 'react';

import { Descriptions, Tag, theme } from 'components';
import type {
  IUserGetTransfersResponse as TransfersDTO,
  IUserFindResponse,
} from '@/app-package';
import {
  TransferState,
  IGenericUser,
  GenericTransferJSON,
  TransferType,
} from '@bb/users';

import { Loading } from '@app/react-common';
import { AuthContext, ListingLabelContext } from 'contexts';
import { TransferStatusTag, transferTypeTagMap } from '../Tables/TransferTags';
import { UserHistoryModalType } from './constants';
import FormContext from '../../UserViewPage/FormContext';
import { UserStatusTag } from '../../../components';

interface UserInfoTableProps {
  userId?: string;
  transfer?: TransfersDTO['transfers'][number];
  type: UserHistoryModalType;
}
const contentTextStyle: React.CSSProperties = {
  color: `${theme.gray8}`,
  fontWeight: 400,
};

const StatusTag = ({
  type,
  transfer,
  user,
}: {
  type: UserHistoryModalType;
  transfer?: TransfersDTO['transfers'][number];
  user?:
    | IUserFindResponse
    | TransfersDTO['transfers'][number]['user']
    | undefined;
}) => {
  const { auth } = useContext(AuthContext);

  if (type === UserHistoryModalType.TRANSFER) {
    return (
      <>
        <TransferStatusTag state={transfer?.state || TransferState.Active} />
        {transfer && transferTypeTagMap[transfer.type]}
      </>
    );
  }

  if (!user) return null;

  const { isDeleted, isUnverified, disabled } = user as IGenericUser;

  if (isDeleted) {
    return (
      <>
        <Tag color="red">Deleted</Tag>
        {transfer?.type && transferTypeTagMap[transfer.type]}
      </>
    );
  }
  if (transfer?.state === TransferState.Pending) {
    // assumes outgoing transfer
    return (
      <>
        <Tag color="orange">Pending</Tag>
        {transferTypeTagMap[TransferType.Outgoing]}
      </>
    );
  }
  return (
    <UserStatusTag
      disabled={disabled}
      isUnverified={isUnverified}
      displayActive={auth.org.type === 'brokerage'}
      inline
    />
  );
};

const UserInfoTable = ({ userId, transfer, type }: UserInfoTableProps) => {
  const [user, setUser] = useState<
    IUserFindResponse | TransfersDTO['transfers'][number]['user'] | undefined
  >(transfer?.user);

  const {
    fetchedUserState,
    isOutgoingTransfer,
    isIncomingTransfer,
    fetchedUserUnverified,
  } = useContext(FormContext);

  const { getBoardName } = useContext(ListingLabelContext);

  useEffect(() => {
    if (type === UserHistoryModalType.TRANSFER) {
      setUser(transfer.user);
    }
  }, [transfer?.user, type]);

  useEffect(() => {
    if (fetchedUserState && type === UserHistoryModalType.USER) {
      setUser({
        firstName: fetchedUserState.firstName || '',
        lastName: fetchedUserState.lastName || '',
        role: fetchedUserState.role,
        address: fetchedUserState.address || '',
        email: fetchedUserState.email || '',
        disabled: fetchedUserState.disabled,
        isDeleted: fetchedUserState.isDeleted,
        isUnverified: fetchedUserUnverified,
        mls_numbers: [],
        location: '',
        org: '',
      });
    }
  }, [type, fetchedUserState]);

  if (!user) return <Loading />;

  let boardText;
  if (type === UserHistoryModalType.TRANSFER) {
    boardText = user.mls_numbers
      .map(mls => `${getBoardName(mls.board, false)}: ${mls.number}`)
      .join(', ');
  } else {
    boardText =
      fetchedUserState.memberNumbers[0]?.board === 'NO_BOARD'
        ? `No Board: ${fetchedUserState.memberNumbers[0].number}`
        : fetchedUserState.memberNumbers
            .filter(({ board, number }) => !!board && !!number)
            .map(
              ({ board, number }) => `${getBoardName(board, false)}: ${number}`,
            )
            .join(', ');
  }

  const newTransfer: GenericTransferJSON = {
    state:
      isOutgoingTransfer || isIncomingTransfer
        ? TransferState.Pending
        : undefined, // fixes missing state at UserHistoryModalType.USER
    ...transfer,
  };

  return (
    <Descriptions
      title=""
      column={1}
      bordered
      labelStyle={{ width: '20%', padding: 20 }}
    >
      <Descriptions.Item label="User" contentStyle={contentTextStyle}>
        {user.firstName || ''} {user.lastName || ''}
      </Descriptions.Item>
      <Descriptions.Item label="Status" contentStyle={contentTextStyle}>
        <StatusTag user={user} transfer={transfer} type={type} />
      </Descriptions.Item>
      <Descriptions.Item label="Email" contentStyle={contentTextStyle}>
        {user.email || ''}
      </Descriptions.Item>
      <Descriptions.Item label="Member ID" contentStyle={contentTextStyle}>
        {boardText}
      </Descriptions.Item>
    </Descriptions>
  );
};

export default UserInfoTable;
